import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { TabContent, Tabs } from '@hotelplan/components.common.tabs';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import PageTabControls from 'components/core/tabs/TabControls';
import { ITabsItem } from 'components/core/tabs/Tabs.types';
import { useMetaIndexTag } from 'components/domain/meta';
import { duplicateSearchQueryFromLocalToSessionStorage } from 'components/domain/search-query';
import { useGetPdpPageMetaDataQuery } from 'graphql/pdp/GetPdpPageMetaData.generated';
import { PdpTabsKey } from './Pdp.types';
import PdpPageLead, { IPdpPageLeadProps } from './PdpPageLead';
import { PdpScrollContextProvider } from './PdpScrollContext';
import useBackToSrl from './useBackToSrl';

export interface IPdpPageLayoutProps extends IPdpPageLeadProps {
  tabItems: ITabsItem<PdpTabsKey>[];
  tabKey: PdpTabsKey;
}

const BackButton = dynamic(
  () =>
    import('components/domain/back-button').then(module => module.BackButton),
  {
    ssr: false,
  }
);

const PdpPageLayout = ({
  tabItems,
  tabKey,
  children,
  ...content
}: IPdpPageLayoutProps): React.ReactElement => {
  const [t] = useTranslation('common');
  const { id } = useObjectId();
  const backToSRL = useBackToSrl();

  const { data } = useGetPdpPageMetaDataQuery({
    variables: { id },
    skip: tabKey !== PdpTabsKey.Description,
  });

  const metaTag = useMetaIndexTag(
    tabKey === PdpTabsKey.Description
      ? { indexed: data.pdp.meta.index, noFollow: data.pdp.meta.noFollow }
      : {}
  );

  // Duplicate search query from local to session storage to have uniq search query for each tab
  useEffect(duplicateSearchQueryFromLocalToSessionStorage, []);

  return (
    <>
      {metaTag}
      {backToSRL ? (
        <BackButton text={t('offers.backLink')} onClick={backToSRL} />
      ) : null}
      <PdpScrollContextProvider>
        <PdpPageLead {...content}>
          <Tabs<PdpTabsKey> defaultKey={tabKey}>
            <PageTabControls<PdpTabsKey> tabItems={tabItems} />
            <TabContent<PdpTabsKey> tabKey={tabKey}>{children}</TabContent>
          </Tabs>
        </PdpPageLead>
      </PdpScrollContextProvider>
    </>
  );
};

export default PdpPageLayout;
